import type { Move } from '@shared/protocol/messages';
import { RULES } from '../combat/rules';
import { CHAPTERS, PRESET_MONSTERS, QUESTS, itemDef, type ChapterDef, type QuestDef } from '../data/presets';
import { cosmeticDef } from '../data/cosmetics';
import { grantExp } from './hero';
import { dailyQuests, newlyReadyDaily } from './daily';
import type { DailyDef } from '../data/daily';
import { newId, type Hero, type Memory, type Monster, type SaveData } from './model';
import { nextTowerFloor, towerFloorOf, towerMonsterId } from './tower';
import { grantBoardRoll } from './board';
import { hasBuiltInName } from './monsters';
import { MAX_ITEM_LEVEL, itemLevel, upgradeCost } from './upgrade';
import { VICTORY_BONUS, type DieFace } from '../combat/dice';
import { t } from '../../i18n';
import { countsForInjury, recordResult } from './injury';

export interface BattleOutcome {
  monster: Monster;
  result: 'win' | 'loss' | 'draw';
  rounds: number;
  roundWinsByMove: Record<Move, number>;
  bestCombo: number;
  /** Hero HP left at the end, 0-100 (% of max HP). Drives the monster's star rating. */
  hpLeftPercent?: number;
  /** Lucky dice rolled during the fight. */
  diceRolls?: number;
}

/** Stars for a win: ★ any win, ★★ with at least half HP left, ★★★ with at least 80% left. */
export function starsFor(o: Pick<BattleOutcome, 'result' | 'hpLeftPercent'>): number {
  if (o.result !== 'win') return 0;
  const hp = o.hpLeftPercent ?? 0;
  return hp >= 80 ? 3 : hp >= 50 ? 2 : 1;
}

/** A lost battle costs this % of the coins on hand (not practice, not the weekly boss raid). */
export const LOSS_COIN_PERCENT = 5;

export interface RewardSummary {
  exp: number;
  coins: number;
  /** Coins taken away for losing (0 when nothing was lost). */
  coinsLost: number;
  levelsGained: number;
  newLevel: number;
  item: string | null;
  /** Practice battles (classroom monsters) never feed progression. */
  practice: boolean;
  questsReady: QuestDef[];
  /** Stars earned this battle (0 = no win or not a preset monster). */
  stars: number;
  /** True when this beat the monster's previous best star rating. */
  newBestStars: boolean;
  /** Story chapters finished by this battle (their coin reward is already paid). */
  chaptersCompleted: ChapterDef[];
  /** Daily quests this battle finished (ready to claim). */
  dailyReady: DailyDef[];
  /** This loss ended a losing streak in an injury (systems/injury.ts). */
  injured: boolean;
  /** Real losses in a row after this battle (0 after a win or an injury). */
  lossStreak: number;
}

/**
 * Story stars needed before a chapter opens (the first book needs none). About
 * two stars a stage: replaying a stage for a better star rating opens the way.
 */
const CHAPTER_STARS = [0, 0, 0, 0, 22, 32, 42, 52, 62];

export function chapterStarsNeeded(index: number): number {
  return CHAPTER_STARS[index] ?? 0;
}

export function totalStars(save: SaveData): number {
  return Object.values(save.monsterStars).reduce((a, b) => a + b, 0);
}

export interface ChapterState {
  def: ChapterDef;
  index: number;
  unlocked: boolean;
  /** Stars needed to open this chapter (0 = none). */
  starsNeeded: number;
  /** The previous chapter is done but more stars are needed. */
  starLocked: boolean;
  complete: boolean;
  beaten: number;
  /** First unbeaten stage (monster id) of an unlocked chapter. */
  nextStage: string | null;
}

/** Story progress, derived from which preset monsters have been beaten. */
export function chapterStates(save: SaveData): ChapterState[] {
  const beaten = new Set(save.defeatedPresets);
  const stars = totalStars(save);
  let prevComplete = true;
  return CHAPTERS.map((def, index) => {
    const count = def.stages.filter((id) => beaten.has(id)).length;
    const complete = count === def.stages.length;
    const starsNeeded = chapterStarsNeeded(index);
    // A chapter already started stays open, whatever the stars (saves from before the gate).
    const starLocked = prevComplete && count === 0 && stars < starsNeeded;
    const unlocked = prevComplete && !starLocked;
    prevComplete = complete;
    return { def, index, unlocked, starsNeeded, starLocked, complete, beaten: count, nextStage: unlocked ? (def.stages.find((id) => !beaten.has(id)) ?? null) : null };
  });
}

/** A stage can be fought once its chapter is unlocked and every earlier stage is beaten. */
export function stageUnlocked(save: SaveData, monsterId: string): boolean {
  const st = chapterStates(save).find((c) => c.def.stages.includes(monsterId));
  if (!st || !st.unlocked) return false;
  const i = st.def.stages.indexOf(monsterId);
  return st.def.stages.slice(0, i).every((id) => save.defeatedPresets.includes(id));
}

/** The next story fight: the first unbeaten stage of the first incomplete chapter. */
export function nextStoryStage(save: SaveData): string | null {
  return chapterStates(save).find((c) => c.unlocked && !c.complete)?.nextStage ?? null;
}

/**
 * The fight behind "Next monster" on the victory card, so a player can keep going
 * without the notebook: the floor above after a tower win, otherwise the next story
 * stage, and the next tower floor once the story is done or waiting on stars.
 */
export function nextFight(save: SaveData, justFought: string): string {
  const floor = towerFloorOf(justFought);
  if (floor !== null) return towerMonsterId(floor + 1);
  const stage = nextStoryStage(save);
  return stage ? `preset:${stage}` : towerMonsterId(nextTowerFloor(save));
}

/** Pays newly completed chapters once. Returns them. */
function completeChapters(save: SaveData): ChapterDef[] {
  const done: ChapterDef[] = [];
  for (const c of chapterStates(save)) {
    if (!c.complete || save.chaptersDone.includes(c.def.id)) continue;
    save.chaptersDone.push(c.def.id);
    save.coins += c.def.reward.coins;
    if (c.def.reward.item && !save.items.includes(c.def.reward.item)) save.items.push(c.def.reward.item);
    addMemory(save, {
      kind: 'quest',
      title: t('Chapter complete: {chapter}', { chapter: t(c.def.title) }),
      text: t('The page is filled in! +{coins} coins.', { coins: c.def.reward.coins }),
      sticker: 'crown',
    });
    done.push(c.def);
  }
  // The whole story is done: the finish time for the fastest-finish ranking (first time only).
  if (done.length && save.story.startedAt !== null && save.story.doneAt === null && save.chaptersDone.length >= CHAPTERS.length) save.story.doneAt = Date.now();
  return done;
}

export function addMemory(save: SaveData, m: Omit<Memory, 'id' | 'at'>): void {
  save.memories.unshift({ id: newId('mem'), at: Date.now(), ...m });
  if (save.memories.length > 60) save.memories.length = 60;
}

export function questProgress(save: SaveData, hero: Hero | null, q: QuestDef): number {
  const v = q.metric === 'level' ? (hero?.level ?? 0) : save.counters[q.metric];
  return Math.min(q.goal, v);
}

export function readyQuests(save: SaveData, hero: Hero | null): QuestDef[] {
  return QUESTS.filter((q) => !save.quests.claimed.includes(q.id) && questProgress(save, hero, q) >= q.goal);
}

/** Apply a finished single-player battle to the save. Mutates `save` and `hero`. */
export function applyBattle(save: SaveData, hero: Hero, o: BattleOutcome): RewardSummary {
  const practice = o.monster.mode === 'classroom';
  const before = new Set(readyQuests(save, hero).map((q) => q.id));
  const dailyBefore = new Set(dailyQuests(save).filter((q) => q.ready).map((q) => q.def.id));
  let exp = 0;
  let coins = 0;
  let coinsLost = 0;
  let item: string | null = null;
  let stars = 0;
  let newBestStars = false;
  let injured = false;
  // The battle is over: it no longer counts as left unfinished.
  save.battleInProgress = null;

  if (!practice) {
    if (countsForInjury(o.monster)) injured = recordResult(hero, o.result);
    // The story clock starts at the first story battle. A save that already beat
    // story monsters before the clock existed counts from its first hero.
    if (save.story.startedAt === null && o.monster.source === 'preset') {
      save.story.startedAt = save.defeatedPresets.length === 0 ? Date.now() : Math.min(Date.now(), ...save.heroes.map((h) => h.createdAt).filter((x) => x > 0));
    }
    const c = save.counters;
    c.battles++;
    c.rockWins += o.roundWinsByMove.rock;
    c.paperWins += o.roundWinsByMove.paper;
    c.scissorsWins += o.roundWinsByMove.scissors;
    c.bestCombo = Math.max(c.bestCombo, o.bestCombo);
    if (o.monster.source === 'boss') c.bossBattles++;
    c.diceRolls += o.diceRolls ?? 0;
    if (o.result === 'win') {
      c.wins++;
      grantBoardRoll(save);
      exp = o.monster.reward.exp;
      coins = o.monster.reward.coins;
      const preset = o.monster.preset && o.monster.source === 'preset' ? PRESET_MONSTERS.find((p) => p.id === o.monster.preset) : undefined;
      if (preset) {
        stars = starsFor(o);
        const best = save.monsterStars[preset.id] ?? 0;
        if (stars > best) {
          save.monsterStars[preset.id] = stars;
          newBestStars = true;
        }
        if (stars === 3) c.threeStarWins++;
      }
      if (o.monster.source === 'hall') c.hallWins++;
      if (o.monster.source === 'dare') c.daresBeaten++;
      const floor = o.monster.source === 'tower' ? towerFloorOf(o.monster.id) : null;
      if (floor !== null) c.towerBest = Math.max(c.towerBest ?? 0, floor);
      if (preset && !save.defeatedPresets.includes(preset.id)) {
        save.defeatedPresets.push(preset.id);
        c.presetsDefeated = save.defeatedPresets.length;
        if (preset.firstWinItem && !save.items.includes(preset.firstWinItem)) {
          item = preset.firstWinItem;
          save.items.push(item);
        }
      }
    } else {
      exp = Math.floor((o.monster.reward.exp * RULES.progression.lossExpPercent) / 100);
      // Losing drops some coins; a draw does not, and the boss raid is meant to be chipped at, not won.
      if (o.result === 'loss' && o.monster.source !== 'boss') {
        coinsLost = Math.floor((save.coins * LOSS_COIN_PERCENT) / 100);
        save.coins -= coinsLost;
      }
    }
  }

  const levelsGained = grantExp(hero, exp);
  save.coins += coins;

  const own = save.monsters.find((m) => m.id === o.monster.id);
  if (own) {
    if (o.result === 'win') own.losses++;
    else if (o.result === 'loss') own.wins++;
  }

  // Memories are stored as text, so they are written in the language active when they happen.
  const names = { hero: hero.name, monster: hasBuiltInName(o.monster) ? t(o.monster.name) : o.monster.name };
  const itemName = item ? itemDef(item)?.name : undefined;
  addMemory(save, {
    kind: 'battle',
    title: o.result === 'win' ? t('{hero} beat {monster}', names) : o.result === 'loss' ? t('{hero} lost to {monster}', names) : t('{hero} tied with {monster}', names),
    text: `${t('{rounds} rounds, best combo ×{combo}.', { rounds: o.rounds, combo: o.bestCombo })}${coinsLost > 0 ? ` ${t('Dropped {n} coins.', { n: coinsLost })}` : ''}${practice ? ` ${t('(practice — classroom rules)')}` : ''}${item ? ` ${t('Found: {item}!', { item: itemName ? t(itemName) : '' })}` : ''}`,
    sticker: o.result === 'win' ? 'star' : o.result === 'loss' ? 'skull' : 'pencil',
  });
  if (levelsGained > 0) {
    addMemory(save, {
      kind: 'level',
      title: t('Level {n}!', { n: hero.level }),
      text: t('{name} grew up a little. +{points} stat points.', { name: hero.name, points: levelsGained * RULES.progression.statPointsPerLevel }),
      sticker: 'crown',
    });
  }

  const chaptersCompleted = practice ? [] : completeChapters(save);
  const questsReady = readyQuests(save, hero).filter((q) => !before.has(q.id));
  const dailyReady = newlyReadyDaily(save, dailyBefore);
  return { exp, coins, coinsLost, levelsGained, newLevel: hero.level, item, practice, questsReady, stars, newBestStars, chaptersCompleted, dailyReady, injured, lossStreak: hero.lossStreak ?? 0 };
}

/** Notes that a battle which counts has started, so leaving it half-way can be charged as a loss. */
export function markBattleStart(save: SaveData, hero: Hero, monster: Monster, now = Date.now()): void {
  save.battleInProgress = countsForInjury(monster) ? { heroId: hero.id, monsterId: monster.id, monsterName: monster.name, at: now } : null;
}

/**
 * A battle that was still running when the page was reloaded or closed (or the
 * battle screen was left without finishing): it counts as a loss, coins and
 * losing streak included, but gives none of the loss EXP. Null when there was none.
 */
export function forfeitUnfinished(save: SaveData, now = Date.now()): { hero: string; monster: string; coinsLost: number; injured: boolean } | null {
  const fight = save.battleInProgress;
  if (!fight) return null;
  save.battleInProgress = null;
  const hero = save.heroes.find((h) => h.id === fight.heroId);
  if (!hero) return null;
  const coinsLost = Math.floor((save.coins * LOSS_COIN_PERCENT) / 100);
  save.coins -= coinsLost;
  save.counters.battles++;
  const injured = recordResult(hero, 'loss', now);
  const monster = fight.monsterName || t('a monster');
  addMemory(save, {
    kind: 'battle',
    title: t('{hero} ran from {monster}', { hero: hero.name, monster }),
    text: `${t('Leaving a battle half-way counts as a loss.')}${coinsLost > 0 ? ` ${t('Dropped {n} coins.', { n: coinsLost })}` : ''}`,
    sticker: 'skull',
  });
  return { hero: hero.name, monster, coinsLost, injured };
}

/** The victory roll: adds VICTORY_BONUS[face]% of a won battle's EXP and coins. Mutates save and hero. */
export function applyVictoryRoll(save: SaveData, hero: Hero, r: Pick<RewardSummary, 'exp' | 'coins'>, face: DieFace): { exp: number; coins: number; percent: number; levelsGained: number } {
  const percent = VICTORY_BONUS[face];
  const exp = Math.floor((r.exp * percent) / 100);
  const coins = Math.floor((r.coins * percent) / 100);
  save.counters.diceRolls++;
  save.coins += coins;
  return { exp, coins, percent, levelsGained: grantExp(hero, exp) };
}

export function claimQuest(save: SaveData, hero: Hero | null, questId: string): QuestDef | null {
  const q = QUESTS.find((x) => x.id === questId);
  if (!q || save.quests.claimed.includes(q.id) || questProgress(save, hero, q) < q.goal) return null;
  save.quests.claimed.push(q.id);
  save.coins += q.reward.coins;
  if (hero) grantExp(hero, q.reward.exp);
  if (q.reward.item && !save.items.includes(q.reward.item)) save.items.push(q.reward.item);
  const rewardItem = q.reward.item ? itemDef(q.reward.item)?.name : undefined;
  const reward = { exp: q.reward.exp, coins: q.reward.coins, item: rewardItem ? t(rewardItem) : '' };
  addMemory(save, {
    kind: 'quest',
    title: t('Quest done: {quest}', { quest: t(q.title) }),
    text: q.reward.item ? t('+{exp} EXP, +{coins} coins, {item}.', reward) : t('+{exp} EXP, +{coins} coins.', reward),
    sticker: 'heart',
  });
  return q;
}

export function buyItem(save: SaveData, itemId: string): boolean {
  const item = itemDef(itemId);
  if (!item || save.items.includes(itemId) || save.coins < item.price) return false;
  save.coins -= item.price;
  save.items.push(itemId);
  addMemory(save, { kind: 'item', title: t('Bought {item}', { item: t(item.name) }), text: t(item.note), sticker: 'star' });
  return true;
}

/** Upgrades an owned item by one level. Returns false when not owned, maxed out or too expensive. */
export function upgradeItem(save: SaveData, id: string): boolean {
  const item = itemDef(id);
  const level = itemLevel(save, id);
  if (!item || !save.items.includes(id) || level >= MAX_ITEM_LEVEL) return false;
  const cost = upgradeCost(item, level);
  if (save.coins < cost) return false;
  save.coins -= cost;
  save.itemLevels[id] = level + 1;
  addMemory(save, { kind: 'item', title: t('{item} +{n}!', { item: t(item.name), n: level + 1 }), text: t('Upgraded for {coins} coins.', { coins: cost }), sticker: 'star' });
  return true;
}

/** Buys a stationery-shop cosmetic with coins. A new stamp is put on straight away. */
export function buyCosmetic(save: SaveData, id: string): boolean {
  const def = cosmeticDef(id);
  if (!def || def.prize || def.premium || save.cosmetics.owned.includes(id) || save.coins < def.price) return false;
  save.coins -= def.price;
  save.cosmetics.owned.push(id);
  if (def.kind === 'stamp') save.cosmetics.stamp = id;
  addMemory(save, { kind: 'item', title: t('Bought {item}', { item: t(def.name) }), text: t(def.note), sticker: 'star' });
  return true;
}

/** Picks the victory stamp (an owned one, or null for the plain stamp). */
export function chooseStamp(save: SaveData, id: string | null): boolean {
  if (id !== null && (!save.cosmetics.owned.includes(id) || cosmeticDef(id)?.kind !== 'stamp')) return false;
  save.cosmetics.stamp = id;
  return true;
}

/** Ink colours bought in the stationery shop, in the order they were bought. */
export function ownedPens(save: SaveData): string[] {
  return save.cosmetics.owned.flatMap((id) => {
    const def = cosmeticDef(id);
    if (def?.kind !== 'pen') return [];
    return [def.cycle ? `cycle:${def.id}` : def.color];
  });
}
